import { useState } from 'react';
import { Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Spinner, Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { FormSection, SectionLabel, useConfirm, useThemeColors, Icon } from '@kiips/ui';
import type { Attachment, AttachmentAnchor } from '@my-fullstack-app/itinerary-types';
import type { AiQuotaBucket } from '@my-fullstack-app/trpc-types';
import { useAttachmentMutations } from '../../hooks/useAttachments';
import { canExtractBookingFromAttachment } from './canExtractBookingFromAttachment';
import { canExtractVaultDocument } from './canExtractVaultDocument';

/**
 * Attachments UI block (ADR-0060). Drop into any detail page that
 * needs a "Files" / "Receipts" / "Documents" section. Composition:
 *
 *   <AttachmentsSection
 *     tripId={trip.id}
 *     attachments={attachments}
 *     anchor={{ kind: 'booking', entityId: journey.id }}
 *     defaultLabel="confirmation"
 *     canEdit={canEdit}
 *     title="Files"
 *     emptyHint="Attach a confirmation PDF or boarding pass"
 *   />
 *
 * The component reads `useAttachmentMutations(tripId)` to handle
 * picker / upload / download / remove. Filter the attachments list
 * upstream via `getAttachmentsFor(trip, filter)` so this component
 * stays domain-agnostic.
 */

const ICON_FOR_MIME: Record<string, IconName> = {
  'application/pdf': 'picture-as-pdf',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/heic': 'image',
  'image/heif': 'image',
  'image/webp': 'image',
  'message/rfc822': 'mail-outline',
};

function iconForAttachment(a: Attachment): IconName {
  return ICON_FOR_MIME[a.mimeType] ?? 'insert-drive-file';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  tripId: string;
  attachments: Attachment[];
  /** Where new uploads should anchor — booking PDFs to a journey,
   *  receipts to a place, etc. Omit for trip-level Vault uploads. */
  anchor?: AttachmentAnchor | undefined;
  /** Default `label` applied to new uploads (UI niceness — server
   *  doesn't care). */
  defaultLabel?: Attachment['label'] | undefined;
  /** Section heading shown above the list. Pass null to suppress the
   *  built-in heading entirely — useful when the consumer renders its
   *  own (e.g. trip-level Vault on the Review tab). */
  title?: string | null | undefined;
  /** Single-line hint shown when the list is empty. */
  emptyHint?: string | undefined;
  /** Disabled when the caller has read-only access to the trip. */
  canEdit: boolean;
  /** ADR-0081 — when present and `cap > 0`, the parser-eligible rows
   *  show an "Extract booking" action that opens the parse-and-review
   *  modal. Pass null / undefined to hide the action entirely (the
   *  default — generation-only screens don't surface this). */
  parseQuotaStatus?: AiQuotaBucket | null | undefined;
  /** ADR-0083 — when present and `cap > 0`, rows labelled with a
   *  registered `DocumentSchema` (passport, visa, insurance,
   *  vaccination, …) show an "Extract details" action that opens the
   *  same modal route as the booking parser; the route dispatches by
   *  label. */
  extractQuotaStatus?: AiQuotaBucket | null | undefined;
}

export function AttachmentsSection({
  tripId,
  attachments,
  anchor,
  defaultLabel,
  title = 'Files',
  emptyHint = 'No files attached yet.',
  canEdit,
  parseQuotaStatus,
  extractQuotaStatus,
}: Props) {
  const router = useRouter();
  const theme = useThemeColors();
  const confirm = useConfirm();
  const { upload, download, remove } = useAttachmentMutations(tripId);
  const [busyAction, setBusyAction] = useState<'uploading' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async () => {
    setError(null);
    setBusyAction('uploading');
    try {
      await upload(anchor, defaultLabel);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setError(msg);
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <YStack gap={6}>
      {title ? <SectionLabel marginTop={6}>{title}</SectionLabel> : null}
      <FormSection>
        {attachments.length === 0 ? (
          <XStack paddingHorizontal={16} paddingVertical={14}>
            <Text fontSize={13} color="$textSubdued">{emptyHint}</Text>
          </XStack>
        ) : (
          attachments.map((a) => {
            // ADR-0081 booking parser + ADR-0083 vault extractor both
            // route to the same `/trips/[id]/vault/[attachmentId]/extract`
            // modal; the modal dispatches by label internally. The action
            // is shown when either predicate fires.
            const canExtractBooking = canExtractBookingFromAttachment(a, {
              canEdit,
              parseQuotaStatus,
            });
            const canExtractVault = canExtractVaultDocument(a, {
              canEdit,
              extractQuotaStatus,
            });
            const canExtract = canExtractBooking || canExtractVault;
            return (
              <AttachmentRow
                key={a.id}
                attachment={a}
                canEdit={canEdit}
                onOpen={() => {
                  if (a.state !== 'uploaded') return;
                  void download(a).catch((e) => {
                    setError(e instanceof Error ? e.message : 'Failed to open');
                  });
                }}
                onRemove={async () => {
                  const ok = await confirm({
                    title: 'Remove file',
                    message: `Remove "${a.filename}"? This cannot be undone.`,
                    confirmLabel: 'Remove',
                    tone: 'danger',
                  });
                  if (!ok) return;
                  void remove(a.id).catch((e) =>
                    setError(e instanceof Error ? e.message : 'Remove failed')
                  );
                }}
                onExtract={
                  canExtract
                    ? () => {
                        router.push(`/trips/${tripId}/vault/${a.id}/extract`);
                      }
                    : undefined
                }
              />
            );
          })
        )}
      </FormSection>
      {canEdit ? (
        <Pressable
          onPress={handleUpload}
          disabled={busyAction === 'uploading'}
          accessibilityLabel="Add attachment"
          style={({ pressed }) => [
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingVertical: 12,
              paddingHorizontal: 16,
              borderRadius: 14,
              backgroundColor: theme.surface,
              opacity: busyAction === 'uploading' ? 0.6 : pressed ? 0.85 : 1,
            },
          ]}
        >
          {busyAction === 'uploading' ? (
            <Spinner size="small" color="$textOnSurface" />
          ) : (
            <Icon name="upload-file" size={18} color={theme.textOnSurface} />
          )}
          <Text fontSize={14} fontWeight="600" color="$textOnSurface">
            {busyAction === 'uploading' ? 'Uploading…' : 'Add file'}
          </Text>
        </Pressable>
      ) : null}
      {error ? (
        <XStack gap={6} alignItems="center" paddingHorizontal={4}>
          <Icon name="error-outline" size={14} color={theme.destructive} />
          <Text fontSize={12} color="$destructive">{error}</Text>
        </XStack>
      ) : null}
    </YStack>
  );
}

interface RowProps {
  attachment: Attachment;
  canEdit: boolean;
  onOpen: () => void;
  onRemove: () => void;
  /** When defined, the row renders an "Extract booking" action button.
   *  Gated upstream on mime / quota / canEdit so the row stays dumb. */
  onExtract?: (() => void) | undefined;
}

function AttachmentRow({ attachment, canEdit, onOpen, onRemove, onExtract }: RowProps) {
  const theme = useThemeColors();
  const isPending = attachment.state === 'pending';
  return (
    <XStack
      alignItems="center"
      paddingHorizontal={12}
      paddingVertical={10}
      gap={12}
    >
      <YStack
        width={36}
        height={36}
        borderRadius={12}
        backgroundColor="$surfaceCanvas"
        alignItems="center"
        justifyContent="center"
      >
        <Icon name={iconForAttachment(attachment)} size={20} color={theme.textOnSurface} />
      </YStack>
      <Pressable
        accessibilityLabel={`Open ${attachment.filename}`}
        onPress={onOpen}
        disabled={isPending}
        style={({ pressed }) => [
          { flex: 1, minWidth: 0 },
          pressed && !isPending ? { opacity: 0.7 } : null,
        ]}
      >
        <Text fontSize={14} fontWeight="600" numberOfLines={1} color="$textOnSurface">
          {attachment.filename}
        </Text>
        <XStack gap={6} alignItems="center" marginTop={2}>
          <Text fontSize={11} color="$textSubdued">
            {formatBytes(attachment.size)}
          </Text>
          {isPending ? (
            <>
              <Text fontSize={11} color="$textSubdued">·</Text>
              <Spinner size="small" color="$textSubdued" />
              <Text fontSize={11} color="$textSubdued">Uploading</Text>
            </>
          ) : null}
          {attachment.label && !isPending ? (
            <>
              <Text fontSize={11} color="$textSubdued">·</Text>
              <Text fontSize={11} color="$textSubdued">{attachment.label}</Text>
            </>
          ) : null}
        </XStack>
      </Pressable>
      {onExtract ? (
        <Pressable
          onPress={onExtract}
          accessibilityLabel={`Extract booking from ${attachment.filename}`}
          hitSlop={8}
          style={({ pressed }) => [
            {
              padding: 4,
              borderRadius: 10,
            },
            pressed ? { opacity: 0.7 } : null,
          ]}
        >
          <Icon name="auto-awesome" size={18} color={theme.textOnSurface} />
        </Pressable>
      ) : null}
      {canEdit ? (
        <Pressable
          onPress={onRemove}
          accessibilityLabel={`Remove ${attachment.filename}`}
          hitSlop={8}
          style={({ pressed }) => [
            {
              padding: 4,
              borderRadius: 10,
            },
            pressed ? { opacity: 0.7 } : null,
          ]}
        >
          <Icon name="close" size={18} color={theme.textSubdued} />
        </Pressable>
      ) : null}
    </XStack>
  );
}
